import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc, runTransaction, increment, updateDoc, getDocs, deleteDoc, setDoc, query, where, limit } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import VideoUpload from '../components/VideoUpload';
import ImageUpload from '../components/ImageUpload';

// Import Playfair Display font in index.html if not already present
// <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap" rel="stylesheet">

// Color palette
const palette = {
  atomicTangerine: '#FCA574',
  burntSienna: '#DC8A72',
  sandyBrown: '#FDB675',
  jasmine: '#FEE881',
  coral: '#FC896B',
  sunglow: '#FECC6E',
  salmon: '#E6786C',
  darkBlue: '#23406e',
};

const sunriseGradient = 'bg-gradient-to-b from-[#FCA574] via-[#FDB675] to-[#FEE881]';

const ShareStory = () => {
  const { currentUser } = useAuth();
  const [storyText, setStoryText] = useState('');
  const [storyVideo, setStoryVideo] = useState(null); // legacy, keep for compatibility
  const [storyVideoPlayableURL, setStoryVideoPlayableURL] = useState(null); // new: download URL
  const [storyVideoStoragePath, setStoryVideoStoragePath] = useState(null); // new: storage path
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [globalOrg, setGlobalOrg] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [metrics, setMetrics] = useState({
    spiritualDiscussions: 0,
    heardGospel: 0,
    experiencedLove: 0,
    believersEncouraged: 0,
    newFollowers: 0
  });
  const [sharing, setSharing] = useState({
    organizations: {} // Will hold org IDs as keys with boolean values
  });
  const [isWorldwide, setIsWorldwide] = useState(false);
  const [showWorldwideModal, setShowWorldwideModal] = useState(false);
  const [worldwideUnderstandChecked, setWorldwideUnderstandChecked] = useState(false);
  const [worldwideNoClosedCountryChecked, setWorldwideNoClosedCountryChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [storyId, setStoryId] = useState(() => uuidv4());
  const navigate = useNavigate();
  const [orgsOpen, setOrgsOpen] = useState(false);
  const [step, setStep] = useState(0); // 0: verse, 1: story, 2: metrics, 3: orgs
  const [storyType, setStoryType] = useState(null); // 'video' or 'text'
  const [storyPhoto, setStoryPhoto] = useState(null); // for text story

  // Update the organization fetching logic
  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!currentUser?.uid) return;

      try {
        // First, load global organization
        try {
          const { query, where, limit, getDocs } = await import('firebase/firestore');
          const globalOrgQuery = query(
            collection(db, 'organizations'),
            where('type', '==', 'global'),
            limit(1)
          );
          const globalOrgSnapshot = await getDocs(globalOrgQuery);
          
          if (globalOrgSnapshot.empty) {
            // Fallback: check for isGlobal flag
            const fallbackQuery = query(
              collection(db, 'organizations'),
              where('isGlobal', '==', true),
              limit(1)
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            
            if (!fallbackSnapshot.empty) {
              const orgData = fallbackSnapshot.docs[0].data();
              setGlobalOrg({
                id: fallbackSnapshot.docs[0].id,
                name: orgData?.name || 'Together On Mission',
                displayName: 'Worldwide - Everyone',
                status: orgData?.status || 'active',
                type: 'global',
                isGlobal: true,
              });
            }
          } else {
            const orgData = globalOrgSnapshot.docs[0].data();
            setGlobalOrg({
              id: globalOrgSnapshot.docs[0].id,
              name: orgData?.name || 'Together On Mission',
              displayName: 'Worldwide - Everyone',
              status: orgData?.status || 'active',
              type: 'global',
              isGlobal: true,
            });
          }
        } catch (error) {
          console.error('Error loading global organization:', error);
        }

        console.log('Fetching organizations for user:', currentUser.uid);
        
        // Get user's organizations from subcollection
        const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
        const userOrgsSnap = await getDocs(userOrgsRef);
        
        console.log('Found organization docs:', userOrgsSnap.docs.length);

        // Get organization details for active memberships
        const activeOrgs = await Promise.all(
          userOrgsSnap.docs
            .filter(doc => doc.data().status === 'active')
            .map(async (membershipDoc) => {
              const membershipData = membershipDoc.data();
              const orgDoc = await getDoc(doc(db, 'organizations', membershipDoc.id));
              
              if (!orgDoc.exists()) {
                console.warn('Organization not found:', membershipDoc.id);
                // Clean up invalid reference
                await deleteDoc(doc(db, 'users', currentUser.uid, 'organizations', membershipDoc.id));
                return null;
              }

              const orgData = orgDoc.data();
              // Skip global org if it's already loaded separately
              if (globalOrg && membershipDoc.id === globalOrg.id) {
                return null;
              }

              return {
                id: membershipDoc.id,
                name: orgData.name,
                displayName: orgData.displayName || orgData.name,
                role: membershipData.role,
                status: membershipData.status
              };
            })
        );

        // Filter out any null values and log the results
        const validOrgs = activeOrgs.filter(org => org !== null);
        console.log('Valid organizations:', validOrgs);
        setUserOrganizations(validOrgs);

        // Initialize sharing state with valid organizations (not global org - that's handled separately)
        const initialOrgSharing = {};
        validOrgs.forEach(org => {
          initialOrgSharing[org.id] = false;
        });
        
        setSharing(prev => {
          const newSharing = {
            ...prev,
            organizations: initialOrgSharing
          };
          
          // Sync isWorldwide with sharing state if global org was previously selected
          if (globalOrg && prev.organizations && prev.organizations[globalOrg.id]) {
            setIsWorldwide(true);
            newSharing.organizations[globalOrg.id] = true;
          }
          
          return newSharing;
        });

      } catch (error) {
        console.error('Error fetching organizations:', error);
        // Only show UI error if it's a critical error that affects functionality
        if (error.code === 'permission-denied') {
          setSubmitMessage('You do not have permission to access organizations.');
        }
      }
    };

    fetchUserOrgs();
  }, [currentUser]);

  // Add debug output to see what's happening
  useEffect(() => {
    console.log('Debug Info:', {
      userId: currentUser?.uid,
      organizationsFound: userOrganizations.length,
      organizationIds: userOrganizations.map(org => org.id).join(', '),
      globalOrg: globalOrg ? globalOrg.id : null
    });
  }, [currentUser?.uid, userOrganizations, globalOrg]);

  // Add this effect to fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          setUserProfile(userSnap.data());
        } else {
          console.error('User profile not found');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [currentUser?.uid]);

  useEffect(() => {
    console.log('[ShareStory] storyVideo state updated:', storyVideo);
  }, [storyVideo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      console.log('[ShareStory] Submitting story...');
      
      // Verify authentication before proceeding
      if (!currentUser?.uid) {
        setSubmitMessage('You must be logged in to share a story.');
        setIsSubmitting(false);
        return;
      }
      
      // Double-check Firebase auth state
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || firebaseUser.uid !== currentUser.uid) {
        console.error('[ShareStory] Auth mismatch:', {
          firebaseUserUid: firebaseUser?.uid,
          currentUserUid: currentUser.uid
        });
        setSubmitMessage('Authentication error. Please refresh and try again.');
        setIsSubmitting(false);
        return;
      }
      
      console.log('[ShareStory] Auth verified:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email
      });
      
      if (!storyText.trim()) {
        setSubmitMessage('Story text is required.');
        setIsSubmitting(false);
        return;
      }
      console.log('[ShareStory] Story text filled:', storyText);
      if (storyVideo) {
        console.log('[ShareStory] Video uploaded:', storyVideo);
      } else {
        console.log('[ShareStory] No video uploaded.');
      }
      console.log('[ShareStory] Metrics:', metrics);
      // Create story data without private flag
      const storyData = {
        id: storyId,
        text: storyText,
        videoURL: storyVideo, // legacy, keep for compatibility
        storagePath: storyVideoStoragePath || '', // new: storage path
        playableURL: storyVideoPlayableURL || '', // new: download URL
        photoURL: storyPhoto, // Add photo URL for text stories
        authorId: currentUser.uid,
        authorName: userProfile?.name || currentUser.displayName,
        authorEmail: currentUser.email,
        authorPhotoURL: currentUser.photoURL,
        createdAt: serverTimestamp(),
        metrics,
        sharing: {
          organizations: Object.entries(sharing.organizations)
            .filter(([_, isSelected]) => isSelected)
            .map(([orgId]) => orgId)
        }
      };
      console.log('[ShareStory] About to save story. Story data:', storyData);

      // Check for pending video compression
      try {
        const pendingCompressionRef = doc(db, 'pendingVideoCompressions', storyId);
        const pendingCompressionSnap = await getDoc(pendingCompressionRef);
        
        // If there's a pending compression, use the compressed video URL
        if (pendingCompressionSnap.exists) {
          const compressionData = pendingCompressionSnap.data();
          storyData.playableURL = compressionData.playableURL;
          storyData.storagePath = compressionData.storagePath;
          storyData.posterURL = compressionData.posterURL; // Add poster URL
          console.log('[ShareStory] Using compressed video URL:', compressionData.playableURL);
          console.log('[ShareStory] Using poster URL:', compressionData.posterURL);
          
          // Clean up the pending compression record
          try {
            await deleteDoc(pendingCompressionRef);
            console.log('[ShareStory] ✅ Deleted pending compression record');
          } catch (deleteError) {
            console.error('[ShareStory] ⚠️ Error deleting pending compression (non-fatal):', deleteError);
            // Continue anyway - compression cleanup isn't critical
          }
        }
      } catch (compressionError) {
        console.error('[ShareStory] ⚠️ Error checking pending compression (non-fatal):', compressionError);
        // Continue anyway - video compression check isn't critical for story creation
      }

      console.log('[ShareStory] Final story data to save:', storyData);

      // Ensure sharing.organizations is an array (convert from object format)
      const selectedOrgIds = Object.entries(sharing.organizations)
        .filter(([_, isSelected]) => isSelected)
        .map(([orgId]) => orgId);
      
      // Update storyData with array format for sharing.organizations
      const completeStoryData = {
        ...storyData,
        sharing: {
          private: false,
          organizations: selectedOrgIds
        },
        status: 'active'
      };

      console.log('[ShareStory] About to save to user collection, completeStoryData keys:', Object.keys(completeStoryData));

      // Save story to user's collection
      try {
        console.log('[ShareStory] Attempting to save to:', `users/${currentUser.uid}/stories/${storyId}`);
        console.log('[ShareStory] Auth UID:', auth.currentUser?.uid);
        console.log('[ShareStory] Current User UID:', currentUser.uid);
        console.log('[ShareStory] Auth match:', auth.currentUser?.uid === currentUser.uid);
        
        // Ensure auth token is fresh before making the write
        // This is critical for the web SDK to properly send auth credentials
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          throw new Error('No authenticated user found');
        }
        
        // Force token refresh to ensure it's valid
        await firebaseUser.getIdToken(true);
        console.log('[ShareStory] Auth token refreshed');
        
        const userStoryRef = doc(db, 'users', currentUser.uid, 'stories', storyId);
        await setDoc(userStoryRef, completeStoryData);
        console.log('[ShareStory] ✅ Saved to user collection');
      } catch (error) {
        console.error('[ShareStory] ❌ Error saving to user collection:', error);
        console.error('[ShareStory] Error code:', error.code);
        console.error('[ShareStory] Error message:', error.message);
        console.error('[ShareStory] Full error:', error);
        throw new Error(`Failed to save story: ${error.message}`);
      }

      // If not private, add to stories-feed
      if (!completeStoryData.sharing.private) {
        try {
          await setDoc(doc(db, 'stories-feed', storyId), completeStoryData);
          console.log('[ShareStory] ✅ Saved to stories-feed');
        } catch (error) {
          console.error('[ShareStory] ❌ Error saving to stories-feed:', error);
          // Don't fail completely if stories-feed fails
        }
      }

      // Create references in selected organizations - save FULL story data (not just metadata)
      if (selectedOrgIds.length > 0) {
        try {
          await Promise.all(selectedOrgIds.map(async (orgId) => {
            const orgStoryRef = doc(db, 'organizations', orgId, 'stories', storyId);
            await setDoc(orgStoryRef, completeStoryData);
            console.log(`[ShareStory] ✅ Saved to organization ${orgId}`);
          }));
        } catch (error) {
          console.error('[ShareStory] ❌ Error saving to organizations:', error);
          // Continue even if some orgs fail
        }
      }

      console.log('[ShareStory] Story uploaded and record created:', storyData);
      console.log('[ShareStory] Video URL being stored:', storyVideo);
      setSubmitMessage('Story shared successfully!');
      navigate('/my-community');
      // Optionally, reset storyId for a new story
      setStoryId(uuidv4());
    } catch (error) {
      console.error('Error sharing story:', error);
      setSubmitMessage('Error sharing story. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMetricChange = (metric, value) => {
    // Ensure value is not negative
    const newValue = Math.max(0, parseInt(value) || 0);
    setMetrics(prev => ({
      ...prev,
      [metric]: newValue
    }));
  };

  // Add this new function to handle unsharing
  const handleUnshareStory = async (storyId, organizationIds) => {
    try {
      // Remove story reference from each organization's stories subcollection
      await Promise.all(organizationIds.map(async (orgId) => {
        // Delete the story reference from organization
        await deleteDoc(doc(db, 'organizations', orgId, 'stories', storyId));

        // Update organization's metrics
        const orgRef = doc(db, 'organizations', orgId);
        await updateDoc(orgRef, {
          'metrics.totalStories': increment(-1),
          lastActivityAt: serverTimestamp()
        });
      }));

      // Update the sharing status in the user's story
      const userStoryRef = doc(db, 'users', currentUser.uid, 'stories', storyId);
      await updateDoc(userStoryRef, {
        'sharing.organizations': organizationIds.reduce((acc, orgId) => ({
          ...acc,
          [orgId]: false
        }), {}),
        lastUpdated: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error unsharing story:', error);
      throw error;
    }
  };

  // Handle worldwide toggle
  const handleWorldwideToggle = (value) => {
    if (value) {
      // User wants to turn ON worldwide - show modal
      setShowWorldwideModal(true);
      setWorldwideUnderstandChecked(false);
      setWorldwideNoClosedCountryChecked(false);
    } else {
      // User wants to turn OFF worldwide - just turn it off
      setIsWorldwide(false);
      // Remove global org from sharing if it was there
      if (globalOrg && sharing.organizations[globalOrg.id]) {
        const newOrgSharing = {
          ...sharing.organizations,
          [globalOrg.id]: false
        };
        setSharing(prev => ({
          ...prev,
          organizations: newOrgSharing
        }));
      }
    }
  };

  const handleWorldwideModalConfirm = () => {
    if (worldwideUnderstandChecked && worldwideNoClosedCountryChecked) {
      setIsWorldwide(true);
      // Add global org to sharing if it exists
      if (globalOrg) {
        const newOrgSharing = {
          ...sharing.organizations,
          [globalOrg.id]: true
        };
        setSharing(prev => ({
          ...prev,
          organizations: newOrgSharing
        }));
      }
      setShowWorldwideModal(false);
      setWorldwideUnderstandChecked(false);
      setWorldwideNoClosedCountryChecked(false);
    }
  };

  const handleWorldwideModalCancel = () => {
    setShowWorldwideModal(false);
    setWorldwideUnderstandChecked(false);
    setWorldwideNoClosedCountryChecked(false);
  };

  // Update the existing handleOrgToggle function to handle both sharing and unsharing
  const handleOrgToggle = async (orgId) => {
    // Don't allow toggling global org directly - use worldwide toggle instead
    if (globalOrg && orgId === globalOrg.id) {
      return;
    }

    try {
      const newOrgSharing = {
        ...sharing.organizations,
        [orgId]: !sharing.organizations[orgId]
      };

      // If this is an existing story (editing mode)
      if (storyId) {
        if (!newOrgSharing[orgId]) {
          // Unsharing
          await handleUnshareStory(storyId, [orgId]);
        } else {
          // Sharing with new organization
          const userStoryRef = doc(db, 'users', currentUser.uid, 'stories', storyId);
          const orgStoriesRef = doc(db, 'organizations', orgId, 'stories', storyId);
          
          await setDoc(orgStoriesRef, {
            storyId,
            userId: currentUser.uid,
            userStoryRef,
            sharedAt: serverTimestamp(),
            status: 'active'
          });

          await updateDoc(doc(db, 'organizations', orgId), {
            'metrics.totalStories': increment(1),
            lastActivityAt: serverTimestamp()
          });
        }
      }

      setSharing(prev => ({
        ...prev,
        organizations: newOrgSharing
      }));
    } catch (error) {
      console.error('Error toggling organization sharing:', error);
      setSubmitMessage('Error updating story sharing. Please try again.');
    }
  };

  // Add function to handle organization admin visibility changes
  const handleOrgVisibilityChange = async (orgId, storyId, isVisible) => {
    try {
      const orgStoryRef = doc(db, 'organizations', orgId, 'stories', storyId);
      await updateDoc(orgStoryRef, {
        visible: isVisible,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating story visibility:', error);
      throw error;
    }
  };

  return (
    <div className={`${sunriseGradient} min-h-screen flex flex-col items-center justify-center py-0 relative`} style={{ minHeight: '100dvh' }}>
      {/* Soft glow behind card */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] h-[60vh] bg-white/30 blur-2xl rounded-full z-0" style={{ filter: 'blur(60px)', pointerEvents: 'none' }} />
      <div className="relative z-10 w-full max-w-lg mx-auto flex flex-col" style={{ marginTop: 'max(2vh, 12px)' }}>
        {/* Stepper/Progress Bar */}
        <div className="flex items-center justify-center mb-6">
          {[0, 1, 2, 3].map((s, i) => (
            <div 
              key={i} 
              className={`flex-1 h-2 mx-1 rounded-full transition-all duration-300 cursor-pointer hover:opacity-80`} 
              style={{ 
                background: step >= s ? palette.darkBlue : palette.sandyBrown, 
                opacity: step >= s ? 1 : 0.3 
              }}
              onClick={() => {
                // Allow navigation back to step 0 (story type selection) from any step
                if (s === 0) {
                  setStep(0);
                  setStoryType(null);
                  setStoryText('');
                  setStoryVideo(null);
                  setStoryVideoPlayableURL(null);
                  setStoryVideoStoragePath(null);
                  setStoryPhoto(null);
                  setMetrics({
                    spiritualDiscussions: 0,
                    heardGospel: 0,
                    experiencedLove: 0,
                    believersEncouraged: 0,
                    newFollowers: 0
                  });
                }
              }}
            ></div>
          ))}
        </div>
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl px-6 py-12 mb-4"
              style={{ boxShadow: `0 8px 32px 0 ${palette.atomicTangerine}33` }}
            >
              <div className="w-full text-center mb-8">
                <span className="block text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: palette.burntSienna, textShadow: '0 2px 8px #fff8, 0 1px 0 #fff' }}>
                  “I will give thanks to you, LORD, with all my heart;
                  I will tell of all <span className='italic'>your wonderful deeds.</span>”
                </span>
                <div className="text-lg" style={{ color: palette.darkBlue, fontFamily: 'Playfair Display, serif', fontWeight: 600, letterSpacing: '0.05em' }}>PSALM 9:1</div>
              </div>
              <div className="flex flex-col gap-4 w-full max-w-xs mx-auto">
                <button
                  type="button"
                  className="w-full py-3 rounded-xl border-2 font-bold text-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ borderColor: palette.darkBlue, color: palette.darkBlue, fontWeight: 700, boxShadow: `0 2px 12px 0 ${palette.sandyBrown}22` }}
                  onClick={() => { setStoryType('video'); setStep(1); }}
                >
                  Create Video
                </button>
                <button
                  type="button"
                  className="w-full py-3 rounded-xl border-2 font-bold text-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ borderColor: palette.darkBlue, color: palette.darkBlue, fontWeight: 700, boxShadow: `0 2px 12px 0 ${palette.sandyBrown}22` }}
                  onClick={() => { setStoryType('text'); setStep(1); }}
                >
                  Write a Story
                </button>
              </div>
            </motion.div>
          )}
          {step === 1 && storyType === 'video' && (
            <motion.div
              key="step1-video"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col flex-1 bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl px-6 py-8 mb-4"
              style={{ boxShadow: `0 8px 32px 0 ${palette.atomicTangerine}33` }}
            >
              <div className="text-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: palette.darkBlue }}>Share a Video Story</h1>
                <p className="text-base" style={{ color: palette.burntSienna }}>Record or upload a video. (Text is optional)</p>
              </div>
              <VideoUpload
                currentVideo={storyVideoPlayableURL}
                onUploadSuccess={({ playableURL, storagePath }) => {
                  setStoryVideoPlayableURL(playableURL);
                  setStoryVideoStoragePath(storagePath);
                  setStoryVideo(playableURL); // for legacy compatibility
                }}
                storyId={storyId}
              />
              <textarea
                rows="3"
                className="block w-full rounded-xl border px-4 py-3 mt-4 text-base focus:ring-2 placeholder:text-gray-400 shadow-sm transition bg-white/90"
                placeholder="Add a caption or context (optional)..."
                value={storyText}
                onChange={(e) => setStoryText(e.target.value)}
                style={{ fontFamily: 'inherit', minHeight: '60px', fontSize: '1.1rem', borderColor: palette.sandyBrown, outlineColor: palette.atomicTangerine, boxShadow: `0 1px 8px 0 ${palette.sandyBrown}22` }}
              />
              <div className="flex-1" />
              <button
                type="button"
                className={`w-full py-3 rounded-xl font-bold shadow-lg bg-transparent transition mt-4 ${(isSubmitting || !storyVideo) && 'opacity-50 cursor-not-allowed'}`}
                style={{ border: `2px solid ${palette.darkBlue}`, color: palette.darkBlue, fontWeight: 700, boxShadow: `0 2px 12px 0 ${palette.sandyBrown}22` }}
                onClick={() => setStep(2)}
                disabled={isSubmitting || !storyVideo}
              >
                Next
              </button>
            </motion.div>
          )}
          {step === 1 && storyType === 'text' && (
            <motion.div
              key="step1-text"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col flex-1 bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl px-6 py-8 mb-4"
              style={{ boxShadow: `0 8px 32px 0 ${palette.atomicTangerine}33` }}
            >
              <div className="text-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: palette.darkBlue }}>Write a Story</h1>
                <p className="text-base" style={{ color: palette.burntSienna }}>Share your story in words and add a photo if you like.</p>
              </div>
              <ImageUpload
                currentImage={storyPhoto}
                onUploadSuccess={setStoryPhoto}
                path={`stories/${storyId}/photoURL`}
                className="w-32 h-32 mx-auto mb-4"
              />
              <textarea
                rows="6"
                className="block w-full rounded-xl border px-4 py-3 mt-4 text-base focus:ring-2 placeholder:text-gray-400 shadow-sm transition bg-white/90"
                placeholder="Type your story here..."
                value={storyText}
                onChange={(e) => setStoryText(e.target.value)}
                style={{ fontFamily: 'inherit', minHeight: '120px', fontSize: '1.1rem', borderColor: palette.sandyBrown, outlineColor: palette.atomicTangerine, boxShadow: `0 1px 8px 0 ${palette.sandyBrown}22` }}
              />
              <div className="flex-1" />
              <button
                type="button"
                className={`w-full py-3 rounded-xl font-bold shadow-lg bg-transparent transition mt-4 ${(isSubmitting || !storyText.trim()) && 'opacity-50 cursor-not-allowed'}`}
                style={{ border: `2px solid ${palette.darkBlue}`, color: palette.darkBlue, fontWeight: 700, boxShadow: `0 2px 12px 0 ${palette.sandyBrown}22` }}
                onClick={() => setStep(2)}
                disabled={isSubmitting || !storyText.trim()}
              >
                Next
              </button>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col flex-1 bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl px-6 py-8 mb-4"
              style={{ boxShadow: `0 8px 32px 0 ${palette.atomicTangerine}33` }}
            >
              <h3 className="text-lg font-semibold mb-3 text-gray-700 text-center">Impact Metrics</h3>
              <div className="bg-gray-50 rounded-xl p-4 mb-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: 'experiencedLove', label: "Experienced God's Love" },
                    { id: 'spiritualDiscussions', label: 'Spiritual Discussions' },
                    { id: 'heardGospel', label: 'Gospel Shared' },
                    { id: 'believersEncouraged', label: 'Believers Encouraged' },
                    { id: 'newFollowers', label: 'Received Jesus' }
                  ].map((metric) => (
                    <div key={metric.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 shadow-sm">
                      <span className="text-sm text-gray-700 font-medium">{metric.label}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleMetricChange(metric.id, metrics[metric.id] - 1)}
                          className="w-5 h-5 flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded hover:bg-gray-200 focus:ring-2 focus:ring-blue-200"
                        >
                          -
                        </button>
                        <span className="w-5 text-center text-sm font-bold">
                          {metrics[metric.id]}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleMetricChange(metric.id, metrics[metric.id] + 1)}
                          className="w-5 h-5 flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded hover:bg-gray-200 focus:ring-2 focus:ring-blue-200"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1" />
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  className="flex-1 py-3 rounded-xl bg-gray-200 text-gray-700 font-bold shadow hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-400 to-green-600 text-white font-bold shadow-lg hover:from-green-500 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 transition"
                  onClick={() => setStep(3)}
                >
                  Next
                </button>
              </div>
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col flex-1 bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl px-6 py-8 mb-4"
              style={{ boxShadow: `0 8px 32px 0 ${palette.atomicTangerine}33` }}
            >
              <form onSubmit={handleSubmit} className="flex flex-col flex-1">
                <h3 className="text-lg font-semibold mb-3 text-gray-700 text-center">Sharing Settings</h3>
                
                {/* Worldwide Sharing Toggle */}
                {globalOrg && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 mr-4">
                        <div className="text-base font-medium text-gray-700 mb-1">🌍 Share Worldwide</div>
                        <div className="text-sm text-gray-500">Share with everyone on the platform</div>
                      </div>
                      <div className="relative inline-block w-11 h-6">
                        <input
                          type="checkbox"
                          id="worldwide-switch"
                          checked={isWorldwide}
                          onChange={(e) => handleWorldwideToggle(e.target.checked)}
                          className="peer appearance-none w-11 h-6 bg-gray-200 rounded-full checked:bg-blue-600 cursor-pointer transition-colors duration-300 focus:ring-2 focus:ring-blue-200"
                        />
                        <label
                          htmlFor="worldwide-switch"
                          className="absolute top-0 left-0 w-6 h-6 bg-white rounded-full border border-gray-300 shadow-sm transition-transform duration-300 peer-checked:translate-x-5 peer-checked:border-blue-600 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Organization Selection */}
                {userOrganizations.length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700 text-center">Share with Organizations</h3>
                    <div className="bg-gray-50 rounded-xl p-4 mb-2">
                      <button
                        type="button"
                        className="flex items-center justify-between w-full text-left focus:outline-none"
                        onClick={() => setOrgsOpen((open) => !open)}
                      >
                        <span className="text-base font-medium text-gray-700">Select Organizations</span>
                        {orgsOpen ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-500 ml-2" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500 ml-2" />
                        )}
                      </button>
                      {orgsOpen && (
                        <div className="space-y-2 mt-3">
                      {userOrganizations.map(org => (
                        <div
                          key={org.id}
                          className="flex items-center justify-between bg-white rounded-lg px-3 py-2 shadow-sm"
                        >
                          <span className="text-sm text-gray-700 font-medium">{org.displayName}</span>
                          <div className="relative inline-block w-11 h-6">
                            <input
                              type="checkbox"
                              id={`org-switch-${org.id}`}
                              checked={sharing.organizations[org.id] || false}
                              onChange={() => handleOrgToggle(org.id)}
                              className="peer appearance-none w-11 h-6 bg-gray-200 rounded-full checked:bg-blue-600 cursor-pointer transition-colors duration-300 focus:ring-2 focus:ring-blue-200"
                            />
                            <label
                              htmlFor={`org-switch-${org.id}`}
                              className="absolute top-0 left-0 w-6 h-6 bg-white rounded-full border border-gray-300 shadow-sm transition-transform duration-300 peer-checked:translate-x-5 peer-checked:border-blue-600 cursor-pointer"
                            />
                          </div>
                        </div>
                      ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
                <div className="flex-1" />
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    className="flex-1 py-3 rounded-xl bg-gray-200 text-gray-700 font-bold shadow hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition"
                    onClick={() => setStep(2)}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !storyText.trim()}
                    className={`flex-1 py-3 rounded-xl font-bold shadow-lg bg-gradient-to-r from-green-400 to-green-600 text-white transition ${(isSubmitting || !storyText.trim()) && 'opacity-50 cursor-not-allowed'}`}
                    style={{ fontWeight: 700 }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        Sharing...
                      </span>
                    ) : 'Share Story'}
                  </button>
                </div>
                {/* Error/Success Feedback */}
                {submitMessage && (
                  <div className={`mt-4 text-center text-sm rounded p-2 ${submitMessage.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {submitMessage}
                  </div>
                )}
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Worldwide Sharing Confirmation Modal */}
      {showWorldwideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Share Worldwide</h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              By sharing worldwide, anyone on earth with this app will be able to access this information, so treat it like public information. You can unshare it later, but that does not prevent people from screenshots, etc.
            </p>
            
            <div className="space-y-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={worldwideUnderstandChecked}
                  onChange={(e) => setWorldwideUnderstandChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex-1">I understand</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={worldwideNoClosedCountryChecked}
                  onChange={(e) => setWorldwideNoClosedCountryChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex-1">
                  The story I am sharing does not mention anyone who lives in a closed country.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleWorldwideModalCancel}
                className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleWorldwideModalConfirm}
                disabled={!worldwideUnderstandChecked || !worldwideNoClosedCountryChecked}
                className={`flex-1 py-2 px-4 font-semibold rounded-lg transition ${
                  worldwideUnderstandChecked && worldwideNoClosedCountryChecked
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareStory; 